import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary, PageErrorBoundary } from './components/ui/ErrorBoundary';
import { LoadingSpinner } from './components/ui/Loading';
import { ScrollToTop } from './components/ui/ScrollToTop';
import { Suspense, lazy, useEffect } from 'react';
import { Toaster } from 'sonner';
import 'react-day-picker/style.css';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { routePreloader } from './utils/routePreloader';

// Eager load critical pages (landing page for SEO/performance)
import LandingPage from './pages/LandingPage';

// Lazy load marketing pages (non-critical)
const PricingPage = lazy(() => import('./pages/PricingPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Lazy load auth pages
const Register = lazy(() => import('./pages/auth/Register'));
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const SecurityResetPassword = lazy(() => import('./pages/auth/SecurityResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const VerificationPending = lazy(() => import('./pages/auth/VerificationPending'));
const OAuthCallback = lazy(() => import('./pages/auth/OAuthCallback'));

// Lazy load onboarding (large component tree)
const OnboardingWizardPage = lazy(() => import('./pages/onboarding/OnboardingWizardPage').then(m => ({ default: m.OnboardingWizardPage })));

// Lazy load dashboard (largest bundle)
const Dashboard = lazy(() => import('./pages/Dashboard'));

// Register routes with preloader
routePreloader.register('dashboard', () => import('./pages/Dashboard'));
routePreloader.register('onboarding', () => import('./pages/onboarding/OnboardingWizardPage').then(m => ({ default: m.OnboardingWizardPage })));
routePreloader.register('login', () => import('./pages/auth/Login'));
routePreloader.register('register', () => import('./pages/auth/Register'));
routePreloader.register('pricing', () => import('./pages/PricingPage'));

// Root route wrapper - shows landing page for all users
function RootRoute() {
  // Preload critical routes on landing page
  useEffect(() => {
    // Preload auth routes (user likely to login/register from landing page)
    routePreloader.preloadOnIdle(['login', 'register'], 'low');
    // Preload pricing page (common next step)
    routePreloader.preloadOnIdle(['pricing'], 'low');
  }, []);

  return <PageErrorBoundary><LandingPage /></PageErrorBoundary>;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <div className='min-h-screen bg-background'>
            <Suspense
              fallback={
                <div className="min-h-screen flex items-center justify-center">
                  <LoadingSpinner size="lg" label="Loading application..." />
                </div>
              }
            >
              <Routes>
                {/* Marketing Pages */}
                <Route path='/' element={<RootRoute />} />
                <Route path='/pricing' element={<PageErrorBoundary><PricingPage /></PageErrorBoundary>} />
                <Route path='/support' element={<PageErrorBoundary><SupportPage /></PageErrorBoundary>} />
                <Route path='/home' element={<Navigate to="/" replace />} />

                {/* Company Pages */}
                <Route path='/contact' element={<PageErrorBoundary><ContactPage /></PageErrorBoundary>} />

                {/* Product Pages */}
                <Route path='/demo' element={<PageErrorBoundary><DemoPage /></PageErrorBoundary>} />

                {/* Resources Pages */}
                <Route path='/status' element={<PageErrorBoundary><StatusPage /></PageErrorBoundary>} />

                {/* Legal Pages */}
                <Route path='/privacy' element={<PageErrorBoundary><PrivacyPage /></PageErrorBoundary>} />
                <Route path='/terms' element={<PageErrorBoundary><TermsPage /></PageErrorBoundary>} />
                <Route path='/cookies' element={<PageErrorBoundary><CookiesPage /></PageErrorBoundary>} />
                <Route path='/security' element={<PageErrorBoundary><SecurityPage /></PageErrorBoundary>} />

                {/* Protected Pages */}
                <Route
                  path='/onboarding'
                  element={
                    <ProtectedRoute>
                      <PageErrorBoundary>
                        <OnboardingWizardPage />
                      </PageErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path='/dashboard'
                  element={
                    <ProtectedRoute>
                      <PageErrorBoundary>
                        <Dashboard />
                      </PageErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* Auth Pages */}
                <Route path='/auth/register' element={<PageErrorBoundary><Register /></PageErrorBoundary>} />
                <Route path='/auth/login' element={<PageErrorBoundary><Login /></PageErrorBoundary>} />
                <Route path='/auth/forgot-password' element={<PageErrorBoundary><ForgotPassword /></PageErrorBoundary>} />
                <Route path='/auth/reset-password' element={<PageErrorBoundary><ResetPassword /></PageErrorBoundary>} />
                <Route path='/auth/security-reset' element={<PageErrorBoundary><SecurityResetPassword /></PageErrorBoundary>} />
                <Route path='/auth/verify-email' element={<PageErrorBoundary><VerifyEmail /></PageErrorBoundary>} />
                <Route
                  path='/auth/verification-pending'
                  element={<PageErrorBoundary><VerificationPending /></PageErrorBoundary>}
                />
                <Route path='/auth/oauth/callback' element={<PageErrorBoundary><OAuthCallback /></PageErrorBoundary>} />
                <Route path='/auth/success' element={<PageErrorBoundary><OAuthCallback /></PageErrorBoundary>} />
                <Route path='/auth/error' element={<PageErrorBoundary><OAuthCallback /></PageErrorBoundary>} />

                {/* 404 - Catch all */}
                <Route path='*' element={<PageErrorBoundary><NotFoundPage /></PageErrorBoundary>} />
              </Routes>
            </Suspense>
            <ScrollToTop />
            <Toaster 
              position="bottom-right"
              richColors
              closeButton
              duration={4000}
            />
          </div>
      </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
